/**
 * The WebGL2 renderer.
 *
 * One pipeline serves both the interactive canvas and the export: the same
 * programs, the same uniforms, the same order. That is deliberate — a preview
 * that is rendered by different code than the export is a preview that lies.
 */

import {
  type Recipe,
  buildCurveLut,
  whiteBalanceMatrix,
} from '@lumos/core';
import type { DecodedImage } from '@lumos/core';
import {
  BASE_FRAGMENT,
  BLUR_FRAGMENT,
  DETAIL_FRAGMENT,
  DOWNSAMPLE_FRAGMENT,
  GEOMETRY_FRAGMENT,
  MAIN_FRAGMENT,
  MAX_MASKS,
  PRESENT_FRAGMENT,
  VERTEX_SHADER,
} from './shaders.ts';

export interface ViewState {
  /** 1 = fit to the viewport. */
  zoom: number;
  /** Pan offset in viewport-normalised units. */
  panX: number;
  panY: number;
  /** `fit` recomputes zoom from the container each frame. */
  mode: 'fit' | 'free';
}

export interface RenderOptions {
  showClipping: boolean;
  /** Index of a mask to visualise, or -1. */
  overlayMask: number;
  overlayStrength: number;
  /** 0..1 wipe position; null disables the before/after split. */
  splitPosition: number | null;
}

interface Target {
  framebuffer: WebGLFramebuffer;
  textures: WebGLTexture[];
  width: number;
  height: number;
}

const BACKGROUND: [number, number, number] = [0.055, 0.035, 0.016];

export class LumosRenderer {
  private gl: WebGL2RenderingContext;
  private programs = new Map<string, WebGLProgram>();
  private uniformCache = new Map<WebGLProgram, Map<string, WebGLUniformLocation | null>>();
  private vao: WebGLVertexArrayObject;

  private sourceTexture: WebGLTexture | null = null;
  private sourceWidth = 0;
  private sourceHeight = 0;

  private targets = new Map<string, Target>();
  private curveTexture: WebGLTexture | null = null;
  private disposed = false;

  /** Set when the context is lost so callers can rebuild rather than spin. */
  contextLost = false;

  constructor(private canvas: HTMLCanvasElement | OffscreenCanvas) {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: false,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });
    if (!gl) {
      throw new Error(
        'This browser could not create a WebGL2 context. Lumos needs WebGL2 for its render pipeline.',
      );
    }
    this.gl = gl;

    // Rendering into half-float targets is what keeps the mid-chain from
    // banding; without it the tone controls quantise visibly in the shadows.
    if (!gl.getExtension('EXT_color_buffer_float')) {
      throw new Error(
        'This browser does not support floating-point render targets (EXT_color_buffer_float).',
      );
    }
    gl.getExtension('OES_texture_float_linear');

    if (canvas instanceof HTMLCanvasElement) {
      canvas.addEventListener('webglcontextlost', this.handleContextLost);
    }

    const vao = gl.createVertexArray();
    if (!vao) throw new Error('Could not allocate a vertex array.');
    this.vao = vao;

    this.compileAll();
  }

  private handleContextLost = (event: Event) => {
    event.preventDefault();
    this.contextLost = true;
  };

  // -------------------------------------------------------------------------
  // Programs
  // -------------------------------------------------------------------------

  private compileAll(): void {
    this.programs.set('geometry', this.link(VERTEX_SHADER, GEOMETRY_FRAGMENT, 'geometry'));
    this.programs.set('base', this.link(VERTEX_SHADER, BASE_FRAGMENT, 'base'));
    this.programs.set('downsample', this.link(VERTEX_SHADER, DOWNSAMPLE_FRAGMENT, 'downsample'));
    this.programs.set('blur', this.link(VERTEX_SHADER, BLUR_FRAGMENT, 'blur'));
    this.programs.set('main', this.link(VERTEX_SHADER, MAIN_FRAGMENT, 'main'));
    this.programs.set('detail', this.link(VERTEX_SHADER, DETAIL_FRAGMENT, 'detail'));
    this.programs.set('present', this.link(VERTEX_SHADER, PRESENT_FRAGMENT, 'present'));
  }

  private link(vertexSource: string, fragmentSource: string, name: string): WebGLProgram {
    const gl = this.gl;
    const program = gl.createProgram();
    if (!program) throw new Error(`Could not create the ${name} program.`);

    const vertex = this.compile(gl.VERTEX_SHADER, vertexSource, `${name}.vert`);
    const fragment = this.compile(gl.FRAGMENT_SHADER, fragmentSource, `${name}.frag`);
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(`Could not link the ${name} program: ${log}`);
    }
    return program;
  }

  private compile(type: number, source: string, name: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type);
    if (!shader) throw new Error(`Could not create shader ${name}.`);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(`Could not compile ${name}: ${log}`);
    }
    return shader;
  }

  private use(name: string): WebGLProgram {
    const program = this.programs.get(name);
    if (!program) throw new Error(`Unknown program "${name}".`);
    this.gl.useProgram(program);
    return program;
  }

  private location(program: WebGLProgram, name: string): WebGLUniformLocation | null {
    let cache = this.uniformCache.get(program);
    if (!cache) {
      cache = new Map();
      this.uniformCache.set(program, cache);
    }
    if (!cache.has(name)) cache.set(name, this.gl.getUniformLocation(program, name));
    return cache.get(name) ?? null;
  }

  // -------------------------------------------------------------------------
  // Resources
  // -------------------------------------------------------------------------

  private createTexture(
    width: number,
    height: number,
    internalFormat: number,
    format: number,
    type: number,
    filter: number,
  ): WebGLTexture {
    const gl = this.gl;
    const texture = gl.createTexture();
    if (!texture) throw new Error('Could not allocate a texture.');
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, width, height, 0, format, type, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return texture;
  }

  /** Gets or resizes a render target, optionally with multiple colour attachments. */
  private target(name: string, width: number, height: number, attachments = 1, byte = false): Target {
    const gl = this.gl;
    width = Math.max(1, Math.round(width));
    height = Math.max(1, Math.round(height));

    const existing = this.targets.get(name);
    if (existing && existing.width === width && existing.height === height) return existing;
    if (existing) this.destroyTarget(existing);

    const framebuffer = gl.createFramebuffer();
    if (!framebuffer) throw new Error('Could not allocate a framebuffer.');
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);

    const textures: WebGLTexture[] = [];
    const drawBuffers: number[] = [];
    for (let i = 0; i < attachments; i += 1) {
      const texture = byte
        ? this.createTexture(width, height, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.LINEAR)
        : this.createTexture(width, height, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT, gl.LINEAR);
      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0 + i,
        gl.TEXTURE_2D,
        texture,
        0,
      );
      textures.push(texture);
      drawBuffers.push(gl.COLOR_ATTACHMENT0 + i);
    }
    gl.drawBuffers(drawBuffers);

    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error(`Render target "${name}" is incomplete (0x${status.toString(16)}).`);
    }

    const created: Target = { framebuffer, textures, width, height };
    this.targets.set(name, created);
    return created;
  }

  private destroyTarget(target: Target): void {
    const gl = this.gl;
    gl.deleteFramebuffer(target.framebuffer);
    for (const texture of target.textures) gl.deleteTexture(texture);
  }

  /** Uploads the decoded scene-linear image. */
  setImage(image: DecodedImage): void {
    const gl = this.gl;
    if (this.sourceTexture) gl.deleteTexture(this.sourceTexture);

    const texture = gl.createTexture();
    if (!texture) throw new Error('Could not allocate the source texture.');
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGB32F,
      image.width,
      image.height,
      0,
      gl.RGB,
      gl.FLOAT,
      image.data,
    );
    // RGB32F is not guaranteed filterable, and this texture is only ever read
    // 1:1 by the copy pass, so nearest is both correct and cheapest.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    this.sourceTexture = texture;
    this.sourceWidth = image.width;
    this.sourceHeight = image.height;
  }

  hasImage(): boolean {
    return this.sourceTexture !== null;
  }

  /** Dimensions of the processed frame for the given recipe, after cropping. */
  processedSize(recipe: Recipe): { width: number; height: number } {
    const swapped = recipe.orientation % 2 === 1;
    const baseWidth = swapped ? this.sourceHeight : this.sourceWidth;
    const baseHeight = swapped ? this.sourceWidth : this.sourceHeight;
    const crop = recipe.crop;
    return {
      width: Math.max(1, Math.round(baseWidth * (crop?.width ?? 1))),
      height: Math.max(1, Math.round(baseHeight * (crop?.height ?? 1))),
    };
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  private drawTo(target: Target | null, width: number, height: number): void {
    const gl = this.gl;
    if (target) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
      gl.viewport(0, 0, target.width, target.height);
    } else {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, width, height);
    }
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  private bind(program: WebGLProgram, name: string, texture: WebGLTexture, unit: number): void {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(this.location(program, name), unit);
  }

  /**
   * Runs the full chain and leaves the result in the `detail` target.
   * Returns the processed dimensions.
   */
  process(recipe: Recipe, image: DecodedImage, options: RenderOptions): { width: number; height: number } {
    if (!this.sourceTexture) throw new Error('No image is loaded.');
    const gl = this.gl;

    const { width, height } = this.processedSize(recipe);

    // --- geometry ---------------------------------------------------------
    const geometry = this.target('geometry', width, height);
    const geometryProgram = this.use('geometry');
    this.bind(geometryProgram, 'uSource', this.sourceTexture, 0);
    const crop = recipe.crop ?? { x: 0, y: 0, width: 1, height: 1 };
    gl.uniform4f(this.location(geometryProgram, 'uCrop'), crop.x, crop.y, crop.width, crop.height);
    gl.uniform1f(this.location(geometryProgram, 'uRotate'), (recipe.rotate * Math.PI) / 180);
    gl.uniform1i(this.location(geometryProgram, 'uOrientation'), recipe.orientation % 4);
    gl.uniform2f(
      this.location(geometryProgram, 'uFlip'),
      recipe.flipHorizontal ? -1 : 1,
      recipe.flipVertical ? -1 : 1,
    );
    gl.uniform1f(
      this.location(geometryProgram, 'uSourceAspect'),
      this.sourceWidth / Math.max(1, this.sourceHeight),
    );
    this.drawTo(geometry, width, height);

    // --- base -------------------------------------------------------------
    const base = this.target('base', width, height);
    const baseProgram = this.use('base');
    this.bind(baseProgram, 'uSource', geometry.textures[0], 0);

    const kelvin = recipe.temperature ?? image.asShot.kelvin;
    const tint = recipe.tint ?? image.asShot.tint;
    const wb = whiteBalanceMatrix(kelvin, tint, image.asShot.kelvin, image.asShot.tint);
    // GLSL mat3 uniforms are column-major; the colour matrix is row-major.
    gl.uniformMatrix3fv(this.location(baseProgram, 'uWhiteBalance'), false, new Float32Array([
      wb[0], wb[3], wb[6],
      wb[1], wb[4], wb[7],
      wb[2], wb[5], wb[8],
    ]));
    gl.uniform1f(this.location(baseProgram, 'uExposure'), recipe.exposure);
    gl.uniform1f(this.location(baseProgram, 'uContrast'), recipe.contrast / 100);
    gl.uniform1f(this.location(baseProgram, 'uHighlights'), recipe.highlights / 100);
    gl.uniform1f(this.location(baseProgram, 'uShadows'), recipe.shadows / 100);
    gl.uniform1f(this.location(baseProgram, 'uWhites'), recipe.whites / 100);
    gl.uniform1f(this.location(baseProgram, 'uBlacks'), recipe.blacks / 100);
    // The largest value the levels stretch can produce. The shader needs it to
    // know how much headroom it is folding back in; at neutral it is exactly 1
    // and the fold is skipped.
    const whitePoint = 1 - (recipe.whites / 100) * 0.24;
    const blackPoint = -(recipe.blacks / 100) * 0.16;
    gl.uniform1f(
      this.location(baseProgram, 'uPeak'),
      Math.max(1, (1 - blackPoint) / Math.max(whitePoint - blackPoint, 0.05)),
    );
    this.drawTo(base, width, height);

    // --- blur pyramid ------------------------------------------------------
    const nearWidth = Math.max(1, Math.round(width / 4));
    const nearHeight = Math.max(1, Math.round(height / 4));
    const farWidth = Math.max(1, Math.round(width / 16));
    const farHeight = Math.max(1, Math.round(height / 16));

    const down4 = this.target('down4', nearWidth, nearHeight);
    const downProgram = this.use('downsample');
    this.bind(downProgram, 'uSource', base.textures[0], 0);
    gl.uniform2f(this.location(downProgram, 'uTexel'), 1 / width, 1 / height);
    this.drawTo(down4, nearWidth, nearHeight);

    const blurNear = this.blurTarget('near', down4, nearWidth, nearHeight);

    const down16 = this.target('down16', farWidth, farHeight);
    this.use('downsample');
    this.bind(downProgram, 'uSource', blurNear.textures[0], 0);
    gl.uniform2f(this.location(downProgram, 'uTexel'), 1 / nearWidth, 1 / nearHeight);
    this.drawTo(down16, farWidth, farHeight);

    const blurFar = this.blurTarget('far', down16, farWidth, farHeight);

    // --- main --------------------------------------------------------------
    const main = this.target('main', width, height, 2);
    const mainProgram = this.use('main');
    this.bind(mainProgram, 'uBase', base.textures[0], 0);
    this.bind(mainProgram, 'uBlurNear', blurNear.textures[0], 1);
    this.bind(mainProgram, 'uBlurFar', blurFar.textures[0], 2);
    this.bind(mainProgram, 'uCurveLut', this.updateCurveTexture(recipe), 3);
    this.setMainUniforms(mainProgram, recipe, width, height, options);
    this.drawTo(main, width, height);

    // --- detail ------------------------------------------------------------
    const detail = this.target('detail', width, height, 1, true);
    const detailProgram = this.use('detail');
    this.bind(detailProgram, 'uSource', main.textures[0], 0);
    this.bind(detailProgram, 'uControl', main.textures[1], 1);
    gl.uniform2f(this.location(detailProgram, 'uTexel'), 1 / width, 1 / height);
    gl.uniform1f(this.location(detailProgram, 'uSharpenAmount'), recipe.sharpenAmount / 100);
    gl.uniform1f(this.location(detailProgram, 'uSharpenRadius'), recipe.sharpenRadius);
    gl.uniform1f(this.location(detailProgram, 'uSharpenDetail'), recipe.sharpenDetail / 100);
    gl.uniform1f(this.location(detailProgram, 'uSharpenMasking'), recipe.sharpenMasking / 100);
    gl.uniform1f(this.location(detailProgram, 'uNoiseLuminance'), recipe.noiseLuminance / 100);
    gl.uniform1f(this.location(detailProgram, 'uNoiseDetail'), recipe.noiseLuminanceDetail / 100);
    gl.uniform1f(this.location(detailProgram, 'uNoiseColor'), recipe.noiseColor / 100);
    gl.uniform1f(this.location(detailProgram, 'uShowClipping'), options.showClipping ? 1 : 0);
    gl.uniform1f(this.location(detailProgram, 'uOverlayStrength'), options.overlayStrength);
    this.drawTo(detail, width, height);

    return { width, height };
  }

  private blurTarget(name: string, source: Target, width: number, height: number): Target {
    const gl = this.gl;
    const horizontal = this.target(`blur-${name}-h`, width, height);
    const program = this.use('blur');
    this.bind(program, 'uSource', source.textures[0], 0);
    gl.uniform2f(this.location(program, 'uDirection'), 1 / width, 0);
    this.drawTo(horizontal, width, height);

    const vertical = this.target(`blur-${name}-v`, width, height);
    this.use('blur');
    this.bind(program, 'uSource', horizontal.textures[0], 0);
    gl.uniform2f(this.location(program, 'uDirection'), 0, 1 / height);
    this.drawTo(vertical, width, height);
    return vertical;
  }

  private setMainUniforms(
    program: WebGLProgram,
    recipe: Recipe,
    width: number,
    height: number,
    options: RenderOptions,
  ): void {
    const gl = this.gl;
    const at = (name: string) => this.location(program, name);

    gl.uniform2f(at('uTexel'), 1 / width, 1 / height);
    gl.uniform1f(at('uAspect'), width / Math.max(1, height));
    gl.uniform1f(at('uTexture'), recipe.texture / 100);
    gl.uniform1f(at('uClarity'), recipe.clarity / 100);
    gl.uniform1f(at('uDehaze'), recipe.dehaze / 100);
    gl.uniform1f(at('uVibrance'), recipe.vibrance / 100);
    gl.uniform1f(at('uSaturation'), recipe.saturation / 100);
    gl.uniform1f(at('uSeed'), 17.31);

    const bands = ['red', 'orange', 'yellow', 'green', 'aqua', 'blue', 'purple', 'magenta'] as const;
    const hue = new Float32Array(8);
    const saturation = new Float32Array(8);
    const luminance = new Float32Array(8);
    bands.forEach((band, index) => {
      hue[index] = recipe.hsl[band].hue / 100;
      saturation[index] = recipe.hsl[band].saturation / 100;
      luminance[index] = recipe.hsl[band].luminance / 100;
    });
    gl.uniform1fv(at('uHslHue'), hue);
    gl.uniform1fv(at('uHslSat'), saturation);
    gl.uniform1fv(at('uHslLum'), luminance);

    const tintOf = (zone: 'shadows' | 'midtones' | 'highlights' | 'global') => {
      const wheel = recipe.grading[zone];
      const rgb = hueToRgb(wheel.hue);
      const strength = wheel.saturation / 100;
      return [rgb[0] * strength, rgb[1] * strength, rgb[2] * strength] as const;
    };
    const shadows = tintOf('shadows');
    const midtones = tintOf('midtones');
    const highlights = tintOf('highlights');
    const global = tintOf('global');
    gl.uniform3f(at('uGradeShadows'), shadows[0], shadows[1], shadows[2]);
    gl.uniform3f(at('uGradeMidtones'), midtones[0], midtones[1], midtones[2]);
    gl.uniform3f(at('uGradeHighlights'), highlights[0], highlights[1], highlights[2]);
    gl.uniform3f(at('uGradeGlobal'), global[0], global[1], global[2]);
    const globalLuminance = recipe.grading.global.luminance / 100;
    gl.uniform3f(
      at('uGradeLuminance'),
      recipe.grading.shadows.luminance / 100 + globalLuminance,
      recipe.grading.midtones.luminance / 100 + globalLuminance,
      recipe.grading.highlights.luminance / 100 + globalLuminance,
    );
    gl.uniform1f(at('uGradeBlending'), recipe.grading.blending / 100);
    gl.uniform1f(at('uGradeBalance'), recipe.grading.balance / 100);

    gl.uniform1f(at('uVignetteAmount'), recipe.vignetteAmount / 100);
    gl.uniform1f(at('uVignetteMidpoint'), recipe.vignetteMidpoint / 100);
    gl.uniform1f(at('uVignetteFeather'), recipe.vignetteFeather / 100);
    gl.uniform1f(at('uVignetteRoundness'), recipe.vignetteRoundness / 100);
    gl.uniform1f(at('uGrainAmount'), recipe.grainAmount / 100);
    gl.uniform1f(at('uGrainSize'), recipe.grainSize / 100);

    // --- masks -------------------------------------------------------------
    const active = recipe.masks.filter((mask) => mask.enabled).slice(0, MAX_MASKS);
    gl.uniform1i(at('uMaskCount'), active.length);
    gl.uniform1i(
      at('uOverlayMask'),
      options.overlayMask >= 0 ? active.findIndex((mask) => mask === recipe.masks[options.overlayMask]) : -1,
    );

    const kinds = new Int32Array(MAX_MASKS);
    const inverts = new Int32Array(MAX_MASKS);
    const feathers = new Float32Array(MAX_MASKS);
    const opacities = new Float32Array(MAX_MASKS);
    const radial = new Float32Array(MAX_MASKS * 4);
    const angles = new Float32Array(MAX_MASKS);
    const linear = new Float32Array(MAX_MASKS * 4);
    const luma = new Float32Array(MAX_MASKS * 4);
    const colour = new Float32Array(MAX_MASKS * 3);
    const toneA = new Float32Array(MAX_MASKS * 4);
    const toneB = new Float32Array(MAX_MASKS * 4);
    const colourAdj = new Float32Array(MAX_MASKS * 4);
    const detailAdj = new Float32Array(MAX_MASKS * 2);

    const kindIndex = { radial: 0, linear: 1, luminance: 2, color: 3 } as const;

    active.forEach((mask, i) => {
      kinds[i] = kindIndex[mask.kind];
      inverts[i] = mask.invert ? 1 : 0;
      feathers[i] = Math.max(0.01, mask.feather / 100);
      opacities[i] = mask.opacity / 100;

      radial.set([mask.radial.cx, mask.radial.cy, mask.radial.rx, mask.radial.ry], i * 4);
      angles[i] = (mask.radial.angle * Math.PI) / 180;
      linear.set([mask.linear.x1, mask.linear.y1, mask.linear.x2, mask.linear.y2], i * 4);
      luma.set(
        [mask.luminance.low, mask.luminance.high, mask.luminance.lowFeather, mask.luminance.highFeather],
        i * 4,
      );
      colour.set([mask.color.hue / 360, mask.color.hueRange / 180, mask.color.minSaturation], i * 3);

      const a = mask.adjustments;
      toneA.set([a.exposure, a.contrast / 100, a.highlights / 100, a.shadows / 100], i * 4);
      toneB.set([a.whites / 100, a.blacks / 100, a.temperature / 100, a.tint / 100], i * 4);
      colourAdj.set([a.saturation / 100, a.vibrance / 100, a.clarity / 100, a.texture / 100], i * 4);
      detailAdj.set([a.sharpness / 100, a.noise / 100], i * 2);
    });

    gl.uniform1iv(at('uMaskKind'), kinds);
    gl.uniform1iv(at('uMaskInvert'), inverts);
    gl.uniform1fv(at('uMaskFeather'), feathers);
    gl.uniform1fv(at('uMaskOpacity'), opacities);
    gl.uniform4fv(at('uMaskRadial'), radial);
    gl.uniform1fv(at('uMaskAngle'), angles);
    gl.uniform4fv(at('uMaskLinear'), linear);
    gl.uniform4fv(at('uMaskLuma'), luma);
    gl.uniform3fv(at('uMaskColor'), colour);
    gl.uniform4fv(at('uMaskToneA'), toneA);
    gl.uniform4fv(at('uMaskToneB'), toneB);
    gl.uniform4fv(at('uMaskColorAdj'), colourAdj);
    gl.uniform2fv(at('uMaskDetail'), detailAdj);
  }

  /** Rebuilds the 1024-entry curve lookup texture. */
  private updateCurveTexture(recipe: Recipe): WebGLTexture {
    const gl = this.gl;
    const size = 1024;
    if (!this.curveTexture) {
      this.curveTexture = this.createTexture(size, 1, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT, gl.LINEAR);
    }

    const master = buildCurveLut(recipe.curves.rgb, size);
    const red = buildCurveLut(recipe.curves.red, size);
    const green = buildCurveLut(recipe.curves.green, size);
    const blue = buildCurveLut(recipe.curves.blue, size);

    const data = new Uint16Array(size * 4);
    for (let i = 0; i < size; i += 1) {
      data[i * 4] = toHalf(master[i]);
      data[i * 4 + 1] = toHalf(red[i]);
      data[i * 4 + 2] = toHalf(green[i]);
      data[i * 4 + 3] = toHalf(blue[i]);
    }

    gl.bindTexture(gl.TEXTURE_2D, this.curveTexture);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, size, 1, gl.RGBA, gl.HALF_FLOAT, data);
    return this.curveTexture;
  }

  /** Draws the processed frame to the canvas with the current view transform. */
  present(view: ViewState, options: RenderOptions, canvasWidth: number, canvasHeight: number): void {
    const gl = this.gl;
    const detail = this.targets.get('detail');
    const base = this.targets.get('base');
    if (!detail) return;

    const program = this.use('present');
    this.bind(program, 'uProcessed', detail.textures[0], 0);
    this.bind(program, 'uOriginal', (base ?? detail).textures[0], 1);

    const imageAspect = detail.width / detail.height;
    const canvasAspect = canvasWidth / canvasHeight;

    // Fit the frame, then apply zoom and pan around the centre.
    let scaleX: number;
    let scaleY: number;
    if (imageAspect > canvasAspect) {
      scaleX = 1;
      scaleY = canvasAspect / imageAspect;
    } else {
      scaleX = imageAspect / canvasAspect;
      scaleY = 1;
    }
    scaleX *= view.zoom;
    scaleY *= view.zoom;

    const offsetX = (1 - scaleX) / 2 + view.panX;
    const offsetY = (1 - scaleY) / 2 - view.panY;

    gl.uniform2f(this.location(program, 'uScale'), scaleX, scaleY);
    gl.uniform2f(this.location(program, 'uOffset'), offsetX, offsetY);
    gl.uniform1f(this.location(program, 'uSplit'), options.splitPosition ?? 0.5);
    gl.uniform1f(this.location(program, 'uSplitEnabled'), options.splitPosition === null ? 0 : 1);
    gl.uniform3f(this.location(program, 'uBackground'), BACKGROUND[0], BACKGROUND[1], BACKGROUND[2]);

    this.drawTo(null, canvasWidth, canvasHeight);
  }

  /**
   * Reads the processed frame back as RGBA bytes.
   * Used for the histogram, the planner's proxy image and export.
   */
  readProcessed(): { data: Uint8ClampedArray<ArrayBuffer>; width: number; height: number } | null {
    const detail = this.targets.get('detail');
    if (!detail) return null;
    const gl = this.gl;
    const data = new Uint8ClampedArray(detail.width * detail.height * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, detail.framebuffer);
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    gl.readPixels(0, 0, detail.width, detail.height, gl.RGBA, gl.UNSIGNED_BYTE, data);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    // No flip: readPixels starts at the framebuffer's bottom row, and the
    // bottom row is where the source's first (top) row landed — see the note in
    // the present shader. The buffer is already top-down.
    return { data, width: detail.width, height: detail.height };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const gl = this.gl;
    for (const target of this.targets.values()) this.destroyTarget(target);
    this.targets.clear();
    if (this.sourceTexture) gl.deleteTexture(this.sourceTexture);
    if (this.curveTexture) gl.deleteTexture(this.curveTexture);
    for (const program of this.programs.values()) gl.deleteProgram(program);
    this.programs.clear();
    gl.deleteVertexArray(this.vao);
    if (this.canvas instanceof HTMLCanvasElement) {
      this.canvas.removeEventListener('webglcontextlost', this.handleContextLost);
    }
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}

/** Fully saturated RGB for a hue in degrees, used by the grading wheels. */
function hueToRgb(degrees: number): [number, number, number] {
  const h = (((degrees % 360) + 360) % 360) / 60;
  const x = 1 - Math.abs((h % 2) - 1);
  if (h < 1) return [1, x, 0];
  if (h < 2) return [x, 1, 0];
  if (h < 3) return [0, 1, x];
  if (h < 4) return [0, x, 1];
  if (h < 5) return [x, 0, 1];
  return [1, 0, x];
}

const floatView = new Float32Array(1);
const intView = new Int32Array(floatView.buffer);

/** IEEE 754 binary32 to binary16, for the half-float curve texture. */
function toHalf(value: number): number {
  floatView[0] = value;
  const bits = intView[0];
  const sign = (bits >> 16) & 0x8000;
  let exponent = ((bits >> 23) & 0xff) - 112;
  let mantissa = bits & 0x7fffff;

  if (exponent <= 0) {
    if (exponent < -10) return sign;
    mantissa |= 0x800000;
    const shift = 14 - exponent;
    return sign | (mantissa >> shift);
  }
  if (exponent >= 0x1f) return sign | 0x7c00;
  return sign | (exponent << 10) | (mantissa >> 13);
}
